/**
 * Formatting Utilities
 * Helper functions for date, currency, and other formatting
 */

/**
 * Parse an incoming date value into a Date without shifting the calendar day.
 *
 * The API returns date-only fields as bare "YYYY-MM-DD" strings. `new Date(str)`
 * parses those as UTC midnight, which in any negative-offset (US) timezone lands
 * on the previous evening — so a class starting "2026-10-01" would display as
 * "Sep 30". For date-only strings we build the Date from local components to
 * keep the day intact. Datetime strings (with a time/offset) are left to the
 * native parser so their timezone semantics are preserved.
 * @param {string|Date} date
 * @returns {Date}
 */
const parseDateValue = (date) => {
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (match) {
      const [, y, m, d] = match;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }
  return new Date(date);
};

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export const formatDate = (date, options = {}) => {
  if (!date) return "-";

  const dateObj = parseDateValue(date);

  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  };

  return new Intl.DateTimeFormat("en-US", defaultOptions).format(dateObj);
};

/**
 * Format currency amount
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = "USD") => {
  if (amount === null || amount === undefined) return "-";

  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

/**
 * Format date and time
 * @param {string|Date} dateTime - DateTime to format
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return "-";

  const dateObj = dateTime instanceof Date ? dateTime : new Date(dateTime);

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(dateObj);
};

/**
 * Format relative time (e.g., "2 days ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return "-";

  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now - dateObj;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;

  return formatDate(dateObj);
};

/**
 * Whether an enrollment/registration is recent enough to flag as "New".
 *
 * Used to highlight newly-registered participants on rosters. Defaults to a
 * 14-day window. Accepts a Date, an ISO datetime, or a bare YYYY-MM-DD string
 * (parsed via the same day-safe path as formatDate). Returns false for missing
 * or unparseable values so a bad date never spuriously flags a student.
 * @param {string|Date} enrolledAt - When the participant registered
 * @param {number} [windowDays=14] - How many days the badge stays visible
 * @returns {boolean}
 */
export const isNewRegistration = (enrolledAt, windowDays = 14) => {
  if (!enrolledAt) return false;

  const dateObj = parseDateValue(enrolledAt);
  const time = dateObj.getTime();
  if (Number.isNaN(time)) return false;

  const ageMs = Date.now() - time;
  if (ageMs < 0) return false; // future-dated: not "new", just wrong
  return ageMs <= windowDays * 24 * 60 * 60 * 1000;
};

/**
 * Format number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "-";
  return new Intl.NumberFormat("en-US").format(num);
};

/**
 * Grade options matching backend Grade enum.
 * Use this everywhere a grade dropdown is needed.
 */
/**
 * Sort a list of classes by name for any picker a human reads.
 *
 * Classes come back from the API in creation order, which turned every
 * class dropdown into a scroll-and-squint exercise once a site had more
 * than a handful. Numbers compare as numbers, so "Session 2" precedes
 * "Session 10" rather than following it.
 *
 * Returns a new array; the input is left alone.
 *
 * @param {Array<{name?: string}>} list
 * @returns {Array} sorted copy, or an empty array for a non-array input
 */
export const sortClassesByName = (list) =>
  Array.isArray(list)
    ? [...list].sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      )
    : [];

export const GRADE_OPTIONS = [
  { value: "pre_k", label: "PRE-K" },
  { value: "k", label: "K" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "6", label: "6" },
  { value: "7", label: "7" },
  { value: "8", label: "8" },
  { value: "9", label: "9" },
  { value: "10", label: "10" },
  { value: "11", label: "11" },
  { value: "12", label: "12" },
];

/**
 * Format grade value for display
 * Backend stores: "pre_k", "k", "1", "2", etc.
 * Display as: "PRE-K", "K", "1", "2", etc.
 * @param {string} grade - Grade value from API
 * @returns {string} Formatted grade for display
 */
export const formatGrade = (grade) => {
  if (!grade) return "";
  if (grade === "pre_k") return "PRE-K";
  if (grade === "k") return "K";
  return grade.toUpperCase();
};

/**
 * Jersey size options matching backend JerseySize enum.
 * Use this everywhere a jersey size dropdown is needed.
 */
export const JERSEY_SIZE_OPTIONS = [
  { value: "youth_xxs", label: "Youth XXS" },
  { value: "youth_xs", label: "Youth XS" },
  { value: "youth_s", label: "Youth S" },
  { value: "youth_m", label: "Youth M" },
  { value: "youth_l", label: "Youth L" },
  { value: "youth_xl", label: "Youth XL" },
  { value: "s", label: "S" },
  { value: "m", label: "M" },
  { value: "l", label: "L" },
  { value: "xl", label: "XL" },
];

/**
 * Format jersey size value for display.
 * Backend stores: "youth_m", "xl", etc.
 * Display as: "Youth M", "XL", etc.
 * @param {string} size - Jersey size value from API
 * @returns {string} Formatted jersey size for display
 */
export const formatJerseySize = (size) => {
  if (!size) return "";
  const match = JERSEY_SIZE_OPTIONS.find((o) => o.value === size.toLowerCase());
  return match ? match.label : size.toUpperCase();
};

/**
 * Badge label + Tailwind classes for a class's lifecycle status.
 * A completed class (end date passed / marked complete) wins over is_active,
 * which the nightly job flips to false — otherwise it would mislabel as "Draft".
 * @param {{ status?: string, is_active?: boolean }} cls
 * @returns {{ label: string, className: string }}
 */
export const getClassStatusBadge = (cls) => {
  if (cls?.status === "completed") {
    return { label: "Completed", className: "bg-gray-100 text-gray-600" };
  }
  if (cls?.is_active) {
    return { label: "Active", className: "bg-[#DFF5E8] text-status-success" };
  }
  return { label: "Draft", className: "bg-amber-100 text-amber-700" };
};
